import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, NotebookPen } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, trackEvent } from "@/lib/api";

export const TemplatePreviewDialog = ({ open, onOpenChange }) => {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!open || data) return;
        api.get("/public/templates/preview")
            .then((r) => setData(r.data))
            .catch(() => setData({ items: [] }));
    }, [open, data]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[85vh] flex flex-col"
                data-testid="template-preview-dialog"
            >
                <DialogHeader>
                    <div className="mono-label mb-1 inline-flex items-center gap-2">
                        <NotebookPen className="h-3 w-3" strokeWidth={1.5} /> Template preview
                    </div>
                    <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight">
                        Inside the SOC 2 starter template
                    </DialogTitle>
                    <DialogDescription>
                        A real slice of the control baseline every new engagement starts
                        with — cross-mapped to ISO 27001, PCI DSS, HIPAA and more.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto -mx-2 px-2">
                    {!data ? (
                        <div className="py-16 grid place-items-center">
                            <Loader2 className="h-6 w-6 animate-spin" strokeWidth={1.5} />
                        </div>
                    ) : (
                        <div className="divide-y divide-border/60">
                            {data.items.map((c) => (
                                <div
                                    key={c.id}
                                    className="py-4 flex gap-4"
                                    data-testid={`preview-control-${c.id}`}
                                >
                                    <div className="mono-label w-14 shrink-0 pt-1">{c.code}</div>
                                    <div className="min-w-0">
                                        <div className="font-display font-semibold leading-tight">
                                            {c.title}
                                            <span className="mono-label ml-2 font-normal">{c.category}</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                            {c.description}
                                        </p>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {c.frameworks.slice(0, 5).map((f) => (
                                                <Badge
                                                    key={f}
                                                    variant="outline"
                                                    className="rounded-full font-mono text-xs border-border/70"
                                                >
                                                    {f}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="pt-4 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-sm text-muted-foreground">
                        {data?.total_controls
                            ? `${data.total_controls} controls in the full library — customise every one to your firm.`
                            : "Customise every control to your firm."}
                    </div>
                    <Link to="/register" className="shrink-0">
                        <Button
                            className="rounded-md font-display"
                            data-testid="template-preview-cta"
                            onClick={() =>
                                trackEvent("landing_cta_click", {
                                    location: "template_preview",
                                    label: "get_started",
                                })
                            }
                        >
                            Start With This Template
                            <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                        </Button>
                    </Link>
                </div>
            </DialogContent>
        </Dialog>
    );
};
